import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getSessionUser } from "@/lib/auth-session";
import { toast } from "sonner";
import { useLocale } from "@/hooks/useLocale";
import { tFormat } from "@/lib/i18n/t-format";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Share2, UserPlus, Trash2, Loader2, Eye, Pencil, Play, Link2, Copy, Check, ExternalLink } from "lucide-react";
import { ConfirmDelete } from "@/components/ui/confirm-delete";

type Permission = "view" | "edit" | "play";

interface Collaborator {
  id: string;
  user_id: string;
  permission: Permission;
  invited_email: string | null;
  created_at: string;
  profile?: { full_name: string | null };
}

interface WorldInfo {
  is_public?: boolean;
  is_invite_only?: boolean;
  ticket_cost?: number;
  clone_price?: number;
  creator_pays_gameplay?: boolean;
}

const PERMISSION_CONFIG: Record<Permission, { label: string; icon: any; description: string }> = {
  view: { label: "View", icon: Eye, description: "Can view world & entities" },
  edit: { label: "Edit", icon: Pencil, description: "Can edit world, entities & build" },
  play: { label: "Play", icon: Play, description: "Can view world & start adventures" },
};

export function ShareWorldDialog({
  worldId,
  worldName,
  trigger,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: {
  worldId: string;
  worldName: string;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const { t } = useLocale();
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [permission, setPermission] = useState<Permission>("view");
  const [isAdding, setIsAdding] = useState(false);
  const [copied, setCopied] = useState(false);
  const [worldInfo, setWorldInfo] = useState<WorldInfo>({});

  useEffect(() => {
    if (open) {
      loadCollaborators();
      loadWorldInfo();
    }
  }, [open, worldId]);

  const loadWorldInfo = async () => {
    const { data } = await supabase
      .from("worlds")
      .select("is_public, is_invite_only, ticket_cost, clone_price, creator_pays_gameplay")
      .eq("id", worldId)
      .maybeSingle();
    if (data) setWorldInfo(data as WorldInfo);
  };

  const shareUrl = useMemo(() => {
    const base = window.location.origin;
    // Always link to the public preview page (/w/:id) — it handles both
    // authenticated and unauthenticated visitors and shows world details
    // without requiring login. The old /world/:id path is the auth-gated builder.
    return `${base}/w/${worldId}`;
  }, [worldId]);

  const shareText = useMemo(() => {
    const parts = [`Check out "${worldName}"`];
    if (worldInfo.ticket_cost && worldInfo.ticket_cost > 0) {
      parts.push(`(${worldInfo.ticket_cost} credits to play)`);
    } else if (worldInfo.creator_pays_gameplay) {
      parts.push("(Free to play!)");
    }
    return parts.join(" ");
  }, [worldName, worldInfo]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success(t.share_link_copied);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t.share_copy_failed);
    }
  };

  const handleShareNative = () => {
    if (navigator.share) {
      navigator.share({ title: worldName, text: shareText, url: shareUrl }).catch(() => {});
    } else {
      handleCopyLink();
    }
  };

  const loadCollaborators = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("world_collaborators")
        .select("*")
        .eq("world_id", worldId);

      if (error) throw error;

      const userIds = (data || []).map((c: any) => c.user_id);
      const profiles: Record<string, any> = {};
      if (userIds.length > 0) {
        const { data: profileData } = await supabase
          .from("profiles")
          .select("id, full_name")
          .in("id", userIds);
        for (const p of profileData || []) {
          profiles[p.id] = p;
        }
      }

      setCollaborators(
        (data || []).map((c: any) => ({
          ...c,
          profile: profiles[c.user_id] || null,
        }))
      );
    } catch (err: any) {
      toast.error(t.share_load_collab_failed);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!email.trim()) return;
    setIsAdding(true);
    try {
      const user = await getSessionUser();
      if (!user) throw new Error("Not authenticated");

      const { data: targetUserId, error: lookupErr } = await supabase
        .rpc("find_user_id_by_email", { _email: email.trim().toLowerCase() });

      if (lookupErr || !targetUserId) {
        toast.error(t.share_user_not_found);
        return;
      }

      if ((targetUserId as string) === user.id) {
        toast.error(t.share_cannot_add_self);
        return;
      }

      const { error } = await supabase.from("world_collaborators").insert({
        world_id: worldId,
        user_id: targetUserId as string,
        permission,
        invited_by: user.id,
        invited_email: email.trim().toLowerCase(),
      });

      if (error) {
        if (error.code === "23505") {
          toast.error(t.share_already_collab);
        } else {
          throw error;
        }
        return;
      }

      toast.success(tFormat(t.share_collab_added, { email, role: permission }));
      setEmail("");
      loadCollaborators();
    } catch (err: any) {
      toast.error(tFormat(t.share_add_collab_failed, { error: err.message }));
    } finally {
      setIsAdding(false);
    }
  };

  const handleUpdatePermission = async (collabId: string, newPermission: Permission) => {
    try {
      const { error } = await supabase
        .from("world_collaborators")
        .update({ permission: newPermission })
        .eq("id", collabId);

      if (error) throw error;
      setCollaborators((prev) =>
        prev.map((c) => (c.id === collabId ? { ...c, permission: newPermission } : c))
      );
      toast.success(t.share_permission_updated);
    } catch (err: any) {
      toast.error(tFormat(t.share_update_failed, { error: err.message }));
    }
  };

  const handleRemove = async (collabId: string) => {
    try {
      const { error } = await supabase
        .from("world_collaborators")
        .delete()
        .eq("id", collabId);

      if (error) throw error;
      setCollaborators((prev) => prev.filter((c) => c.id !== collabId));
      toast.success(t.share_collab_removed);
    } catch (err: any) {
      toast.error(tFormat(t.share_remove_failed, { error: err.message }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && (
        <DialogTrigger asChild>
          {trigger}
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="w-4 h-4" />
            Share "{worldName}"
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Share Link */}
          <div className="space-y-2">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
              <Link2 className="w-3 h-3" /> Share Link
            </h4>
            <div className="flex gap-2">
              <Input
                readOnly
                value={shareUrl}
                className="flex-1 text-xs font-mono bg-muted/30"
                onClick={(e) => (e.target as HTMLInputElement).select()}
              />
              <Button size="icon" variant="outline" onClick={handleCopyLink} className="shrink-0" aria-label={copied ? "Copied!" : "Copy share link"}>
                {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" className="flex-1 gap-1.5 text-xs" onClick={handleShareNative}>
                <ExternalLink className="w-3 h-3" />
                {navigator.share ? "Share…" : "Copy Link"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-xs"
                onClick={() => {
                  window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, "_blank");
                }}
              >
                𝕏 Post
              </Button>
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {worldInfo.is_public && <Badge variant="secondary" className="text-[10px]">Public</Badge>}
              {worldInfo.is_invite_only && <Badge variant="outline" className="text-[10px]">Invite Only</Badge>}
              {worldInfo.creator_pays_gameplay && <Badge variant="default" className="text-[10px]">Free to Play</Badge>}
              {(worldInfo.ticket_cost ?? 0) > 0 && <Badge variant="secondary" className="text-[10px]">{worldInfo.ticket_cost} cr entry</Badge>}
              <Badge variant="outline" className="text-[10px]">Login required</Badge>
            </div>
          </div>

          <Separator />

          {/* Add collaborator */}
          <div className="space-y-2">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
              <UserPlus className="w-3 h-3" /> Invite Collaborator
            </h4>
            <div className="flex gap-2">
              <Input
                placeholder={t.wb_share_email_placeholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                className="flex-1"
              />
              <Select value={permission} onValueChange={(v) => setPermission(v as Permission)}>
                <SelectTrigger className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PERMISSION_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-1">
                        <config.icon className="w-3 h-3" />
                        {config.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={handleAdd}
              disabled={isAdding || !email.trim()}
              size="sm"
              className="w-full gap-1"
            >
              {isAdding ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <UserPlus className="w-3 h-3" />
              )}
              Add Collaborator
            </Button>
          </div>

          {/* Permission legend */}
          <div className="grid grid-cols-3 gap-2">
            {Object.entries(PERMISSION_CONFIG).map(([key, config]) => (
              <div key={key} className="text-center p-2 rounded-md bg-muted/30">
                <config.icon className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                <p className="text-xs font-medium">{config.label}</p>
                <p className="text-xs text-muted-foreground">{config.description}</p>
              </div>
            ))}
          </div>

          {/* Collaborator list */}
          <div className="space-y-2">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Collaborators ({collaborators.length})
            </h4>
            {isLoading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
              </div>
            ) : collaborators.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No collaborators yet
              </p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-auto">
                {collaborators.map((collab) => {
                  const PermIcon = PERMISSION_CONFIG[collab.permission]?.icon || Eye;
                  return (
                    <div
                      key={collab.id}
                      className="flex items-center gap-2 p-2 rounded-md bg-muted/20 border border-border"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {collab.profile?.full_name || collab.invited_email || "Unknown"}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {collab.invited_email}
                        </p>
                      </div>
                      <Select
                        value={collab.permission}
                        onValueChange={(v) =>
                          handleUpdatePermission(collab.id, v as Permission)
                        }
                      >
                        <SelectTrigger className="w-24 h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(PERMISSION_CONFIG).map(([key, config]) => (
                            <SelectItem key={key} value={key}>
                              <div className="flex items-center gap-1">
                                <config.icon className="w-3 h-3" />
                                {config.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <ConfirmDelete
                        onConfirm={() => handleRemove(collab.id)}
                        title="Remove collaborator?"
                        description={`${collab.profile?.full_name || collab.invited_email || "This user"} will lose access to this world.`}
                        triggerClassName="h-8 w-8 text-destructive hover:text-destructive"
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
